/**
 * EventPolicies — Sprint S (Trust & Compliance)
 *
 * Collapsible Event Terms / Refund Policy / Disclaimer sections on the
 * public event page.
 *
 * Rules (user-approved):
 * - Terms & Disclaimer render ONLY when the organiser provided content.
 * - Refund Policy renders the organiser's custom text when provided;
 *   for PAID events with a blank refund policy, a default statement
 *   aligned with Australian Consumer Law is shown instead.
 * - Free events with no custom refund policy show nothing for that section.
 */
import React from "react";
import { ScrollText, ReceiptText, ShieldAlert } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "./ui/accordion";

export const DEFAULT_PAID_REFUND_POLICY =
  "Refunds are handled by the event organiser in accordance with Australian Consumer Law. " +
  "If an event is cancelled or significantly changed by the organiser, buyers may be entitled " +
  "to a refund or other remedy. Booking/platform fees may be handled according to the event " +
  "organiser's published policy and applicable law. Please contact the organiser for refund requests.";

export default function EventPolicies({ event }) {
  const isPaid = event?.pricing_mode === "paid";
  const terms = (event?.event_terms || "").trim();
  const disclaimer = (event?.event_disclaimer || "").trim();
  const customRefund = (event?.refund_policy || "").trim();
  const refund = customRefund || (isPaid ? DEFAULT_PAID_REFUND_POLICY : "");

  const sections = [
    terms && {
      key: "terms",
      label: "Event terms",
      icon: ScrollText,
      text: terms,
      testId: "policy-terms",
      isDefault: false,
    },
    refund && {
      key: "refund",
      label: "Refund policy",
      icon: ReceiptText,
      text: refund,
      testId: "policy-refund",
      isDefault: !customRefund,
    },
    disclaimer && {
      key: "disclaimer",
      label: "Disclaimer",
      icon: ShieldAlert,
      text: disclaimer,
      testId: "policy-disclaimer",
      isDefault: false,
    },
  ].filter(Boolean);

  if (sections.length === 0) return null;

  return (
    <div className="mt-10" data-testid="event-policies">
      <div className="gt-label mb-3">Terms &amp; policies</div>
      <Accordion type="multiple" className="border border-[var(--gt-line)] rounded-sm divide-y divide-[var(--gt-line)] bg-white">
        {sections.map((s) => (
          <AccordionItem key={s.key} value={s.key} className="border-b-0 px-4" data-testid={s.testId}>
            <AccordionTrigger
              className="text-sm font-semibold hover:no-underline py-3.5"
              data-testid={`${s.testId}-trigger`}
            >
              <span className="inline-flex items-center gap-2">
                <s.icon size={14} className="text-[var(--gt-accent)]" aria-hidden="true" />
                {s.label}
              </span>
            </AccordionTrigger>
            <AccordionContent
              className="text-sm text-[var(--gt-ink-2)] whitespace-pre-wrap leading-relaxed"
              data-testid={`${s.testId}-content`}
            >
              {s.text}
              {s.isDefault && (
                <span className="block mt-2 text-[11px] text-[var(--gt-ink-3)]" data-testid="policy-refund-default-note">
                  Default statement — the organiser has not published a custom refund policy.
                </span>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
